import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  Rocket,
  Video,
  Phone,
  Wallet,
  CalendarDays,
  Flame,
  GraduationCap,
  Check,
  X,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";

const FEATURES = [
  {
    icon: Rocket,
    title: "Personal Guidance For Freelancing",
    desc: "Step-by-step guidance to build your freelancing career, find clients, refine positioning and create long-term income.",
  },
  {
    icon: Video,
    title: "1:1 Zoom Call Support",
    desc: "Direct one-on-one Zoom support sessions for guidance, problem-solving, feedback and clarity whenever needed.",
  },
  {
    icon: Phone,
    title: "Calling Support",
    desc: "Direct support whenever you face challenges, confusion or roadblocks in your learning journey.",
  },
  {
    icon: Wallet,
    title: "Roadmap To Earn ₹30,000 In 30 Days",
    desc: "A structured action plan covering client acquisition, outreach, pricing systems and execution frameworks.",
  },
  {
    icon: CalendarDays,
    title: "Sunday Doubt Clearing Session",
    desc: "Weekly live sessions to ask questions, receive guidance, get feedback and stay on track.",
  },
  {
    icon: Flame,
    title: "Two Live Weekly Growth Sessions",
    desc: "Exclusive sessions on freelancing, content creation, high-income skills, productivity, mindset and growth.",
  },
  {
    icon: GraduationCap,
    title: "Personal Mentorship Access",
    desc: "Direct mentorship, accountability, personalised guidance and support — avoid mistakes and achieve results faster.",
  },
];

const ALONE_VS_ENV = [
  "Mentorship",
  "Accountability",
  "Support",
  "Community",
  "Guidance",
  "Growth Environment",
];

export default function OnePercentClub() {
  const navigate = useNavigate();
  const handleUnlockPremium = () => {
    navigate("/?highlight=premium");
  };
  return (
    <section
      id="one-percent-club"
      data-testid="section-one-percent-club"
      className="py-14 sm:py-20 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A] via-[#1E3A8A] to-[#0F172A] pointer-events-none" />
      <div className="absolute inset-0 gradient-mesh opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-1/3 bg-gradient-to-r from-transparent via-[#06B6D4]/70 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-white">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 rounded-full border border-[#06B6D4]/40 bg-[#06B6D4]/15 px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] text-[#06B6D4] font-semibold"
          >
            <Trophy className="h-3.5 w-3.5" /> 1% Community Club
          </motion.span>
          <h2
            data-testid="opc-headline"
            className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-white text-balance leading-[1.05]"
          >
            Most people watch.
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">Only 1% take action.</span>
              <span className="absolute inset-x-0 bottom-2 h-3 bg-[#06B6D4]/40 -z-0 rounded" />
            </span>
          </h2>
          <p className="mt-6 text-[15px] sm:text-base text-slate-200 leading-relaxed max-w-2xl mx-auto">
            Courses can teach skills. The right environment creates success.
            The 1% Community Club is an exclusive growth ecosystem for
            ambitious students who want guidance, accountability, mentorship,
            networking and real progress.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/5 backdrop-blur border border-white/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-slate-200 font-semibold">
            Exclusive access · Premium Package members only
          </div>
        </div>

        {/* Feature grid */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
          className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                data-testid={`opc-feature-${f.title.toLowerCase().replace(/\W+/g, "-")}`}
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                }}
                className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur p-7"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#06B6D4]/15 text-[#06B6D4]">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-display text-lg font-medium tracking-tight text-white">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  {f.desc}
                </p>
              </motion.div>
            );
          })}

          {/* Exclusive access notice */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 18 },
              show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
            }}
            data-testid="opc-access-notice"
            className="rounded-3xl border border-amber-400/40 bg-amber-400/10 backdrop-blur p-7"
          >
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-amber-300 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" /> Important
            </span>
            <h3 className="mt-4 font-display text-lg font-medium tracking-tight text-white">
              Not available in every package
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex items-center gap-2 text-slate-300">
                <X className="h-4 w-4 text-rose-400" /> E-Lite Package
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <X className="h-4 w-4 text-rose-400" /> Basic Package
              </li>
              <li className="flex items-center gap-2 text-white font-medium">
                <Check className="h-4 w-4 text-emerald-400" strokeWidth={3} />
                Included in Premium Package
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Alone vs Environment */}
        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <div
            data-testid="opc-alone"
            className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur p-8"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
              Most students get
            </div>
            <h3 className="mt-3 font-display text-2xl tracking-tight text-white">
              Just access to courses.
            </h3>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed max-w-md">
              Videos, PDFs, assignments. That's it. Months of confusion and
              trial-and-error without the right people around you.
            </p>
          </div>
          <div
            data-testid="opc-environment"
            className="rounded-3xl border border-[#06B6D4]/40 bg-gradient-to-br from-[#06B6D4]/15 via-[#06B6D4]/10 to-transparent backdrop-blur p-8"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#06B6D4] font-semibold">
              Premium members get
            </div>
            <h3 className="mt-3 font-display text-2xl tracking-tight text-white">
              An entire growth environment.
            </h3>
            <ul className="mt-5 grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
              {ALONE_VS_ENV.map((it) => (
                <li key={it} className="flex items-center gap-2 text-white">
                  <Check className="h-4 w-4 text-[#06B6D4]" strokeWidth={3} />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-16 text-center">
          <h3 className="font-display text-3xl sm:text-4xl tracking-tighter font-semibold text-white">
            Ready to join the top 1%?
          </h3>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            Unlock the Premium Package and get exclusive access to the 1%
            Community Club.
          </p>
          <button
            type="button"
            onClick={handleUnlockPremium}
            data-testid="opc-cta-premium"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-white text-[#0F172A] px-7 py-3.5 text-sm font-semibold hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(255,255,255,0.4)] transition-all duration-300"
          >
            Unlock Premium Package <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
